/**
 * oddit-section-ugc
 * Infinite UGC carousel with centered active card, nav arrows, autoplay, and swipe.
 */
(function () {
  'use strict';

  var SECTION_SELECTOR = '.oddit-section-ugc';
  var SWIPE_THRESHOLD = 30;
  var sectionState = new Map();

  function initSection(section) {
    var track = section.querySelector('.oddit-section-ugc__track');
    var slideNodes = track ? track.querySelectorAll('.oddit-section-ugc__card') : [];
    var prevBtn = section.querySelector('.oddit-section-ugc__nav-btn--prev');
    var nextBtn = section.querySelector('.oddit-section-ugc__nav-btn--next');
    if (!track || slideNodes.length === 0) return;

    var slides = Array.from(slideNodes);
    var count = slides.length;
    var centerPos = Math.floor(count / 2);
    var activeIndex = Math.floor(count / 2);
    var pendingDirection = 0;
    var isAnimating = false;
    var autoplayTimer = null;
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    var autoplayEnabled = section.getAttribute('data-autoplay') === 'true';
    var autoplaySpeed = (parseInt(section.getAttribute('data-autoplay-speed'), 10) || 5) * 1000;

    function getGap() {
      var s = getComputedStyle(track);
      return parseFloat(s.columnGap) || parseFloat(s.gap) || 0;
    }

    /* Reorder DOM so the active slide sits at the physical center position */
    function reorderDOM(active) {
      for (var i = -centerPos; i < count - centerPos; i++) {
        var idx = ((active + i) % count + count) % count;
        track.appendChild(slides[idx]);
      }
      slides.forEach(function (slide, idx) {
        slide.classList.toggle('oddit-section-ugc__card--center', idx === active);
      });
    }

    /* Get the full outer width of an element including margins */
    function outerWidth(el) {
      var style = getComputedStyle(el);
      return el.offsetWidth + parseFloat(style.marginLeft) + parseFloat(style.marginRight);
    }

    /* Calculate translateX to center the physical center child */
    function getCenterX() {
      var carouselWidth = track.parentElement.offsetWidth;
      var children = track.children;
      var gap = getGap();
      var offset = 0;
      for (var i = 0; i < centerPos; i++) {
        offset += outerWidth(children[i]) + gap;
      }
      var centerStyle = getComputedStyle(children[centerPos]);
      var centerMarginLeft = parseFloat(centerStyle.marginLeft) || 0;
      offset += centerMarginLeft;
      return -offset + (carouselWidth - children[centerPos].offsetWidth) / 2;
    }

    /* Snap to center without transition */
    function snapToCenter() {
      track.style.transition = 'none';
      track.style.transform = 'translateX(' + getCenterX() + 'px)';
      track.offsetHeight; /* force reflow */
      track.style.transition = '';
    }

    /* Move by direction: +1 = next, -1 = prev */
    function go(direction) {
      if (isAnimating) return;

      if (reducedMotion.matches) {
        activeIndex = ((activeIndex + direction) % count + count) % count;
        reorderDOM(activeIndex);
        snapToCenter();
        return;
      }

      isAnimating = true;
      pendingDirection = direction;

      var children = track.children;
      var gap = getGap();
      var neighborIdx = centerPos + direction;

      /* Apply center class to incoming card and remove from current
         BEFORE the slide starts so the scale/transform transitions
         happen simultaneously with the track movement */
      children[centerPos].classList.remove('oddit-section-ugc__card--center');
      children[neighborIdx].classList.add('oddit-section-ugc__card--center');

      var shift = (outerWidth(children[centerPos]) + outerWidth(children[neighborIdx])) / 2 + gap;
      track.style.transform = 'translateX(' + (getCenterX() - shift * direction) + 'px)';
    }

    function goNext() { go(1); }
    function goPrev() { go(-1); }

    function onTransitionEnd(e) {
      if (e.target !== track || e.propertyName !== 'transform') return;
      activeIndex = ((activeIndex + pendingDirection) % count + count) % count;
      pendingDirection = 0;
      reorderDOM(activeIndex);
      snapToCenter();
      isAnimating = false;
    }

    track.addEventListener('transitionend', onTransitionEnd);

    /* Autoplay */
    function startAutoplay() {
      stopAutoplay();
      if (!autoplayEnabled) return;
      autoplayTimer = setInterval(goNext, autoplaySpeed);
    }

    function stopAutoplay() {
      if (autoplayTimer) {
        clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
    }

    function killAutoplay() {
      autoplayEnabled = false;
      stopAutoplay();
    }

    /* Pause on hover, kill on click/touch */
    section.addEventListener('mouseenter', function () { stopAutoplay(); });
    section.addEventListener('mouseleave', function () {
      if (autoplayEnabled && !reducedMotion.matches && !document.hidden) startAutoplay();
    });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stopAutoplay();
      else if (autoplayEnabled && !reducedMotion.matches) startAutoplay();
    });

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        killAutoplay();
        goPrev();
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        killAutoplay();
        goNext();
      });
    }

    /* Touch swipe */
    var touchStartX = 0;
    var touchStartY = 0;
    var isSwiping = false;

    track.addEventListener('touchstart', function (e) {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      isSwiping = false;
    }, { passive: true });

    track.addEventListener('touchmove', function (e) {
      if (isSwiping) {
        e.preventDefault();
        return;
      }
      var dx = e.touches[0].clientX - touchStartX;
      var dy = e.touches[0].clientY - touchStartY;
      if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) {
        isSwiping = true;
        e.preventDefault();
      }
    }, { passive: false });

    track.addEventListener('touchend', function (e) {
      var dx = e.changedTouches[0].clientX - touchStartX;
      if (isSwiping && Math.abs(dx) > SWIPE_THRESHOLD) {
        killAutoplay();
        if (dx < 0) goNext(); else goPrev();
      }
      isSwiping = false;
    }, { passive: true });

    /* Recenter on resize */
    var ro = new ResizeObserver(function () {
      if (!isAnimating) snapToCenter();
    });
    ro.observe(section);

    /* Init */
    reorderDOM(activeIndex);
    snapToCenter();
    if (autoplayEnabled && !reducedMotion.matches) startAutoplay();

    sectionState.set(section, {
      stopAutoplay: stopAutoplay,
      ro: ro
    });
  }

  function destroySection(section) {
    var state = sectionState.get(section);
    if (!state) return;
    if (state.stopAutoplay) state.stopAutoplay();
    if (state.ro) state.ro.disconnect();
    sectionState.delete(section);
  }

  document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);

  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (section) destroySection(section);
  });
})();
